/**
 * A template page laid out with sample content — the editor canvas and the
 * library thumbnails draw exactly what a sheet would print, minus the project.
 */

import type { DisplayPage, ImageResource } from '@pointorama/print-engine';
import { SHEET_DOC_SCHEMA_VERSION, defaultViewportSettings, pageViewports, viewportShowsPlan, viewportSettingsKey, type ImageSource, type PrintTemplateSpec, type SheetDoc, type ViewportSettings } from 'shared-types';
import { PLAN_FONT } from '../planFont';
import type { PreviewEnv } from './usePreviewEnv';
import { SAMPLE_FLOORS, SAMPLE_PLAN_BOUNDS, SAMPLE_SOURCES, samplePlanEntities } from './sampleData';

export interface TemplatePreviewOptions {
  /** Brand details for the `{org.*}` placeholders (sample values otherwise), and the user's length unit (`units`). */
  sources?: Partial<typeof SAMPLE_SOURCES>;
  image?: (source: ImageSource) => ImageResource | null;
  missing?: 'empty' | 'marker';
}

/** The sample plan framed in the middle of every plan viewport, at its default scale. */
function sampleViewports(spec: PrintTemplateSpec, pageId: string): Record<string, ViewportSettings> {
  const page = spec.pages.find((p) => p.id === pageId);
  if (!page) return {};
  const center = { x: (SAMPLE_PLAN_BOUNDS.minX + SAMPLE_PLAN_BOUNDS.maxX) / 2, y: (SAMPLE_PLAN_BOUNDS.minY + SAMPLE_PLAN_BOUNDS.maxY) / 2 };
  const out: Record<string, ViewportSettings> = {};
  for (const element of pageViewports(page)) {
    out[viewportSettingsKey(page.id, element.id)] = { ...defaultViewportSettings(element.defaultKind, element.defaultScaleDen), center, floorId: SAMPLE_FLOORS[0].id, capturedAt: new Date(0).toISOString() };
  }
  return out;
}

export function layoutTemplatePreview(env: PreviewEnv, spec: PrintTemplateSpec, pageId: string, options: TemplatePreviewOptions = {}): DisplayPage | null {
  const page = spec.pages.find((p) => p.id === pageId);
  if (!page) return null;
  const single: PrintTemplateSpec = { ...spec, pages: [page] };
  const sheet: SheetDoc = {
    schemaVersion: SHEET_DOC_SCHEMA_VERSION,
    templateRef: { source: 'builtin', id: 'preview', version: 0 },
    fieldValues: {},
    revisions: [{ id: 'r1', rev: 'A', date: '2026-01-15', description: 'First issue', by: 'JA' }],
    floorIds: null,
    viewports: sampleViewports(spec, page.id),
    hiddenPages: [],
  };
  const sources = { ...SAMPLE_SOURCES, ...options.sources };
  const planEntities = samplePlanEntities(sources.units);
  const [laid] = env.engine.layoutDocument({
    spec: single,
    sheet,
    floors: SAMPLE_FLOORS,
    sources,
    date: new Date(),
    missing: options.missing ?? 'marker',
    fonts: env.fonts,
    image: options.image ?? (() => null),
    viewportContent: (_instance, viewport) =>
      viewportShowsPlan(viewport.settings)
        ? env.engine.planToDisplayItems({ entities: planEntities, blocks: [], settings: viewport.settings, widthMm: viewport.element.wMm, heightMm: viewport.element.hMm, fonts: env.fonts, font: PLAN_FONT })
        : null,
  });
  return laid?.page ?? null;
}

export function renderTemplatePreviewSvg(env: PreviewEnv, spec: PrintTemplateSpec, pageId: string, options: TemplatePreviewOptions & { imageHref?: (key: string) => string | null; idPrefix?: string } = {}): string | null {
  const page = layoutTemplatePreview(env, spec, pageId, options);
  if (!page) return null;
  return env.engine.renderSvg(page, { font: env.svgFont, imageHref: options.imageHref ?? (() => null), idPrefix: options.idPrefix, background: '#ffffff' });
}
